<!--
  Rendered directly under DisplayPackOptionsComponent's "Features" box (the
  last section it renders) inside the shared card in billing.component.html —
  a divider + heading, not its own bordered section, per design feedback.
-->
<div class="flex flex-col gap-3 border-t border-gray-200 pt-4">
  <h2 class="text-heading-4 font-semibold text-primary-900">
    {{ "apiAccess.title" | transloco }}
  </h2>

  @if (!useApi) {
    <p class="text-paragraph-s-reg text-greyscale-600">
      {{ "apiAccess.notIncluded" | transloco }}
    </p>
  } @else {
    <p class="text-paragraph-s-reg text-greyscale-600">
      {{ "apiAccess.description" | transloco }}
    </p>

    @if (generatedToken(); as token) {
      <!-- Shown exactly once, right after generation -->
      <div
        class="flex flex-col gap-2 rounded-lg border border-warning-200 bg-warning-50 p-3"
      >
        <span class="text-paragraph-s-reg font-medium text-primary-900">
          {{ "apiAccess.tokenGenerated.title" | transloco }}
        </span>
        <div class="flex flex-col gap-2 sm:flex-row">
          <input
            pInputText
            type="text"
            [value]="token"
            readonly
            class="flex-1 font-mono text-paragraph-xs-reg"
            [fluid]="true"
          />
          <p-button
            [label]="'apiAccess.tokenGenerated.copyButton' | transloco"
            icon="pi pi-copy"
            severity="secondary"
            (onClick)="copyToken()"
          />
        </div>
        <p class="text-paragraph-xs-reg text-warning-800">
          {{ "apiAccess.tokenGenerated.warning" | transloco }}
        </p>
        <div>
          <p-button
            [label]="'apiAccess.tokenGenerated.doneButton' | transloco"
            severity="secondary"
            size="small"
            (onClick)="dismissGeneratedToken()"
          />
        </div>
      </div>
    } @else {
      @if (tokenInfo(); as info) {
        <!-- An active token exists -->
        <div
          class="flex flex-col gap-2 rounded-lg border border-primary-200 bg-primary-50 p-3"
        >
          <div class="flex flex-wrap items-center justify-between gap-2">
            <span class="text-paragraph-s-reg font-medium text-primary-900">
              {{ "apiAccess.activeToken.label" | transloco }}
            </span>
            <span class="font-mono text-paragraph-xs-reg text-greyscale-600">
              {{ "apiAccess.activeToken.prefix" | transloco }}
              {{ info.tokenPrefix }}…
            </span>
          </div>
          <div
            class="flex flex-col gap-1 text-paragraph-xs-reg text-greyscale-600"
          >
            <span>
              {{ "apiAccess.activeToken.createdAt" | transloco }}
              {{ info.createdAt | date: "medium" }}
            </span>
            <span>
              {{ "apiAccess.activeToken.lastUsedAt" | transloco }}
              {{
                info.lastUsedAt
                  ? (info.lastUsedAt | date: "medium")
                  : ("apiAccess.activeToken.neverUsed" | transloco)
              }}
            </span>
            @if (usage(); as u) {
              <span>
                {{ "apiAccess.usage.label" | transloco }}:
                {{
                  u.limit === 0
                    ? ("apiAccess.usage.callsUnlimited"
                      | transloco: { used: u.used })
                    : ("apiAccess.usage.callsOf"
                      | transloco: { used: u.used, limit: u.limit })
                }}
              </span>
            }
          </div>

          @if (confirmingRegenerate()) {
            <div
              class="flex flex-col gap-2 rounded border border-warning-200 bg-warning-50 p-2"
            >
              <span class="text-paragraph-xs-reg text-warning-800">
                {{ "apiAccess.activeToken.regenerateWarning" | transloco }}
              </span>
              <div class="flex gap-2">
                <p-button
                  [label]="'apiAccess.activeToken.regenerateButton' | transloco"
                  severity="warn"
                  size="small"
                  [loading]="loading()"
                  (onClick)="generate()"
                />
                <p-button
                  [label]="
                    'apiAccess.activeToken.revokeConfirm.cancelButton'
                      | transloco
                  "
                  severity="secondary"
                  size="small"
                  (onClick)="cancelRegenerate()"
                />
              </div>
            </div>
          } @else if (confirmingRevoke()) {
            <div
              class="flex flex-col gap-2 rounded border border-error-200 bg-error-50 p-2"
            >
              <span class="text-paragraph-xs-reg text-error-950">
                {{ "apiAccess.activeToken.revokeConfirm.message" | transloco }}
              </span>
              <div class="flex gap-2">
                <p-button
                  [label]="
                    'apiAccess.activeToken.revokeConfirm.confirmButton'
                      | transloco
                  "
                  severity="danger"
                  size="small"
                  [loading]="loading()"
                  (onClick)="revoke()"
                />
                <p-button
                  [label]="
                    'apiAccess.activeToken.revokeConfirm.cancelButton'
                      | transloco
                  "
                  severity="secondary"
                  size="small"
                  (onClick)="cancelRevoke()"
                />
              </div>
            </div>
          } @else {
            <div class="flex gap-2">
              <p-button
                [label]="'apiAccess.activeToken.regenerateButton' | transloco"
                severity="secondary"
                size="small"
                (onClick)="confirmRegenerate()"
              />
              <p-button
                [label]="'apiAccess.activeToken.revokeButton' | transloco"
                severity="danger"
                size="small"
                [outlined]="true"
                (onClick)="confirmRevoke()"
              />
            </div>
          }
        </div>
      } @else {
        <!-- No token yet -->
        <div class="flex flex-col items-start gap-2">
          <p class="text-paragraph-s-reg text-greyscale-600">
            {{ "apiAccess.noToken.message" | transloco }}
          </p>
          <p-button
            [label]="'apiAccess.noToken.generateButton' | transloco"
            icon="pi pi-key"
            [loading]="loading()"
            (onClick)="generate()"
          />
        </div>
      }
    }
  }
</div>
